import React, { useState, useEffect } from 'react';
import { Users, DollarSign, UserPlus, Crown, ChevronLeft, RefreshCw, Search, ChevronRight, ChevronLeft as PrevIcon, Trash2, Calendar, Settings, CheckSquare, AlertTriangle, Database, LogOut, Activity, Shield, Lock, KeyRound, ToggleRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Format an ISO datetime as "2h ago", "3d ago", "Apr 12" etc. Null-safe.
const relativeTime = (iso) => {
  if (!iso) return 'Never';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'Never';
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const EVENT_LABELS = {
  admin_login_success:           { label: 'Admin login',             icon: Shield,    tone: 'text-green-700 bg-green-50 border-green-200' },
  admin_login_failed:            { label: 'Failed login',            icon: AlertTriangle, tone: 'text-orange-700 bg-orange-50 border-orange-200' },
  admin_login_locked:            { label: 'Login locked out',        icon: Lock,      tone: 'text-red-700 bg-red-50 border-red-200' },
  admin_login_blocked:           { label: 'Locked attempt',          icon: Lock,      tone: 'text-red-700 bg-red-50 border-red-200' },
  admin_password_changed:        { label: 'Password changed',        icon: KeyRound,  tone: 'text-brand-accent bg-brand-accent/5 border-brand-border' },
  admin_password_reset_requested:{ label: 'Reset link requested',    icon: KeyRound,  tone: 'text-brand-accent bg-brand-accent/5 border-brand-border' },
  admin_password_reset_completed:{ label: 'Password reset',          icon: KeyRound,  tone: 'text-brand-accent bg-brand-accent/5 border-brand-border' },
  monetization_toggled:          { label: 'Monetization toggled',    icon: ToggleRight, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [tierFilter, setTierFilter] = useState('all');
  const [inactiveOnly, setInactiveOnly] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLogLoading, setActivityLogLoading] = useState(true);
  const [activityFilterSince, setActivityFilterSince] = useState('');
  const [activityFilterUntil, setActivityFilterUntil] = useState('');
  const [activityFilterEvent, setActivityFilterEvent] = useState('');

  useEffect(() => {
    checkAdminAccess();
    fetchStats();
    fetchActivityLog();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [page, sortBy, sortOrder, tierFilter, searchTerm, inactiveOnly]);

  // Refetch the activity log whenever its filters change
  useEffect(() => {
    fetchActivityLog();
  }, [activityFilterSince, activityFilterUntil, activityFilterEvent]);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const user = await response.json();
        if (!user.is_admin) {
          navigate('/');
        }
      } else {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(tierFilter !== 'all' && { tier_filter: tierFilter }),
        ...(searchTerm && { search: searchTerm }),
        ...(inactiveOnly && { inactive_days: 30 })
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    setActivityLogLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (activityFilterSince) params.set('since', new Date(activityFilterSince).toISOString());
      if (activityFilterUntil) params.set('until', new Date(activityFilterUntil).toISOString());
      if (activityFilterEvent) params.set('event', activityFilterEvent);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/activity-log?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setActivityLog(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    } finally {
      setActivityLogLoading(false);
    }
  };

  const handleUpgradeUser = async (userId, tier) => {
    if (!window.confirm(`Are you sure you want to change this user's tier to ${tier}?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/upgrade?tier=${tier}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        alert('User tier updated successfully!');
        fetchUsers();
        fetchStats();
      } else {
        alert('Failed to update user tier');
      }
    } catch (error) {
      console.error('Failed to upgrade user:', error);
      alert('Failed to update user tier');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ${userEmail} and ALL their data (reflections, insights, action plans).\n\nThis action CANNOT be undone.\n\nAre you absolutely sure?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        alert('User deleted successfully');
        fetchUsers();
        fetchStats();
      } else {
        alert('Failed to delete user');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    }
  };

  const handleSetTrialDate = async (userId, days) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/set-trial-date?days=${days}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        alert(`Trial period set to ${days} days from now`);
        fetchUsers();
      } else {
        alert('Failed to set trial date');
      }
    } catch (error) {
      console.error('Failed to set trial date:', error);
      alert('Failed to set trial date');
    }
  };

  const handleTestTrialExpiry = async () => {
    if (!window.confirm('This will check all trial users and downgrade those with expired trials. Continue?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/test-trial-expiry`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(`Trial expiry check completed. ${data.users_downgraded} users downgraded to Basic.`);
        fetchUsers();
        fetchStats();
      } else {
        alert('Failed to run trial expiry check');
      }
    } catch (error) {
      console.error('Failed to run trial expiry:', error);
      alert('Failed to run trial expiry check');
    }
  };

  const handleMigrateUsers = async () => {
    if (!window.confirm('This will set all existing users (without subscription tier) to Grandfathered status. Continue?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/migrate-existing-users`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(`Migration completed! ${data.users_migrated} users migrated to Grandfathered status.`);
        fetchUsers();
        fetchStats();
      } else {
        alert('Failed to migrate users');
      }
    } catch (error) {
      console.error('Failed to migrate users:', error);
      alert('Failed to migrate users');
    }
  };

  const handleBulkUpdate = async (tier) => {
    if (selectedUsers.length === 0) {
      alert('Please select users first');
      return;
    }

    if (!window.confirm(`Change ${selectedUsers.length} selected users to ${tier}?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-update-tier?tier=${tier}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(selectedUsers),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setSelectedUsers([]);
        setShowBulkActions(false);
        fetchUsers();
        fetchStats();
      } else {
        alert('Failed to bulk update users');
      }
    } catch (error) {
      console.error('Failed to bulk update:', error);
      alert('Failed to bulk update users');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const totalPages = Math.ceil(total / limit);

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Premium Users',
      value: stats?.premium_users || 0,
      icon: Crown,
      color: 'blue',
    },
    {
      title: 'Monthly Revenue',
      value: `£${stats?.monthly_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'green',
    },
    {
      title: 'Recent Signups',
      value: stats?.recent_signups || 0,
      icon: UserPlus,
      color: 'orange',
      subtitle: 'Last 30 days',
    },
  ];

  const tierColors = {
    trial: 'bg-yellow-100 text-yellow-800',
    basic: 'bg-gray-100 text-gray-800',
    premium: 'bg-brand-accent/10 text-brand-accent',
    grandfathered: 'bg-green-100 text-green-800',
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <div className="bg-brand-accent text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                <p className="text-white/80 mt-1">Reflective Mind Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleTestTrialExpiry}
                className="flex items-center space-x-2 px-3 py-2 bg-orange-500 hover:bg-orange-400 rounded-lg transition-colors text-sm"
                title="Test trial expiry for all users"
              >
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden md:inline">Check Trials</span>
              </button>
              <button
                onClick={handleMigrateUsers}
                className="flex items-center space-x-2 px-3 py-2 bg-green-500 hover:bg-green-400 rounded-lg transition-colors text-sm"
                title="Migrate existing users to Grandfathered"
              >
                <CheckSquare className="h-4 w-4" />
                <span className="hidden md:inline">Migrate Users</span>
              </button>
              <button
                onClick={() => navigate('/admin/settings')}
                className="flex items-center space-x-2 px-3 py-2 bg-brand-accent/50 hover:bg-brand-accent-hover rounded-lg transition-colors text-sm"
                title="Settings — Pricing · Site config · Legal · Lite invite codes"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden md:inline">Settings</span>
              </button>
              <button
                onClick={() => navigate('/admin/utilities')}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-500 hover:bg-blue-400 rounded-lg transition-colors text-sm"
                title="Database Utilities"
              >
                <Database className="h-4 w-4" />
                <span className="hidden md:inline">Database</span>
              </button>
              <button
                onClick={() => {
                  fetchStats();
                  fetchUsers();
                }}
                className="flex items-center space-x-2 px-3 py-2 bg-brand-accent hover:bg-brand-accent-hover rounded-lg transition-colors"
              >
                <RefreshCw className="h-5 w-5" />
                <span className="hidden md:inline">Refresh</span>
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Logout from admin panel?')) {
                    localStorage.clear();
                    navigate('/admin/login');
                  }
                }}
                className="flex items-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                        <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                        {stat.subtitle && (
                          <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                        )}
                      </div>
                      <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                        <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tier Breakdown */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">User Tier Breakdown</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors"
                     onClick={() => setTierFilter('trial')}>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.trial_users || 0}</p>
                  <p className="text-sm text-gray-600">Trial</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                     onClick={() => setTierFilter('basic')}>
                  <p className="text-2xl font-bold text-gray-600">{stats?.basic_users || 0}</p>
                  <p className="text-sm text-gray-600">Basic</p>
                </div>
                <div className="text-center p-4 bg-brand-accent/5 rounded-lg cursor-pointer hover:bg-brand-accent/10 transition-colors"
                     onClick={() => setTierFilter('premium')}>
                  <p className="text-2xl font-bold text-brand-accent">{stats?.premium_users || 0}</p>
                  <p className="text-sm text-gray-600">Premium</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                     onClick={() => setTierFilter('grandfathered')}>
                  <p className="text-2xl font-bold text-green-600">{stats?.grandfathered_users || 0}</p>
                  <p className="text-sm text-gray-600">Grandfathered</p>
                </div>
              </div>
            </div>

            {/* Recent Admin Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-brand-border mb-8" data-testid="admin-activity-log">
              <div className="px-6 py-4 border-b border-brand-border flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-brand-accent" />
                    <h2 className="text-lg font-bold text-brand-ink">Recent admin activity</h2>
                  </div>
                  <button
                    onClick={fetchActivityLog}
                    className="text-xs text-brand-accent hover:text-brand-accent-hover transition-colors"
                    data-testid="activity-log-refresh-btn"
                  >
                    Refresh
                  </button>
                </div>
                {/* Date range + event filter */}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-wider text-brand-ink-muted mb-0.5">From</label>
                    <input
                      type="datetime-local"
                      value={activityFilterSince}
                      onChange={(e) => setActivityFilterSince(e.target.value)}
                      className="text-sm px-2 py-1 border border-brand-border rounded-md focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      data-testid="activity-filter-since"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-wider text-brand-ink-muted mb-0.5">To</label>
                    <input
                      type="datetime-local"
                      value={activityFilterUntil}
                      onChange={(e) => setActivityFilterUntil(e.target.value)}
                      className="text-sm px-2 py-1 border border-brand-border rounded-md focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      data-testid="activity-filter-until"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-wider text-brand-ink-muted mb-0.5">Event</label>
                    <select
                      value={activityFilterEvent}
                      onChange={(e) => setActivityFilterEvent(e.target.value)}
                      className="text-sm px-2 py-1 border border-brand-border rounded-md bg-white focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      data-testid="activity-filter-event"
                    >
                      <option value="">All events</option>
                      <option value="admin_login_success">Admin login</option>
                      <option value="admin_login_failed">Failed login</option>
                      <option value="admin_login_locked">Lockouts</option>
                      <option value="admin_password_changed">Password changed</option>
                      <option value="admin_password_reset_requested">Reset requested</option>
                      <option value="admin_password_reset_completed">Reset completed</option>
                      <option value="monetization_toggled">Monetization toggled</option>
                    </select>
                  </div>
                  {(activityFilterSince || activityFilterUntil || activityFilterEvent) && (
                    <button
                      onClick={() => { setActivityFilterSince(''); setActivityFilterUntil(''); setActivityFilterEvent(''); }}
                      className="text-xs text-brand-ink-muted hover:text-brand-ink transition-colors px-2 py-1.5"
                      data-testid="activity-filter-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              {activityLogLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-accent mx-auto"></div>
                </div>
              ) : activityLog.length === 0 ? (
                <div className="p-8 text-center text-sm text-brand-ink-muted">
                  No admin events logged yet.
                </div>
              ) : (
                <ul className="divide-y divide-brand-border/60 max-h-96 overflow-y-auto">
                  {activityLog.map((evt, idx) => {
                    const cfg = EVENT_LABELS[evt.event] || { label: evt.event, icon: Activity, tone: 'text-gray-700 bg-gray-50 border-gray-200' };
                    const Icon = cfg.icon;
                    let detailText = '';
                    if (evt.event === 'monetization_toggled') {
                      detailText = `${evt.details?.from ? 'ON' : 'OFF'} → ${evt.details?.to ? 'ON' : 'OFF'}` +
                        (evt.details?.grandfathered_count ? ` · grandfathered ${evt.details.grandfathered_count}` : '');
                    } else if (evt.event === 'admin_login_failed' || evt.event === 'admin_login_locked') {
                      detailText = `attempt ${evt.details?.attempts || '?'}/${evt.details?.max_attempts || 5}`;
                    }
                    return (
                      <li key={evt.id || idx} className="px-6 py-3 flex items-start gap-3 hover:bg-brand-accent/[0.02] transition-colors">
                        <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border ${cfg.tone}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-sm font-medium text-brand-ink">{cfg.label}</p>
                            <p className="text-xs text-brand-ink-muted">{relativeTime(evt.created_at)}</p>
                          </div>
                          <p className="text-xs text-brand-ink-muted mt-0.5 truncate">
                            <span className="font-mono">{evt.actor_email || 'anonymous'}</span>
                            {evt.ip && <span> · {evt.ip}</span>}
                            {detailText && <span> · {detailText}</span>}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <h2 className="text-xl font-bold text-gray-900">Users</h2>
                  
                  <div className="flex flex-col md:flex-row gap-3 flex-1 md:max-w-2xl">
                    {/* Search */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      />
                    </div>
                    
                    {/* Tier Filter */}
                    <select
                      value={tierFilter}
                      onChange={(e) => setTierFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    >
                      <option value="all">All Tiers</option>
                      <option value="trial">Trial</option>
                      <option value="basic">Basic</option>
                      <option value="premium">Premium</option>
                      <option value="grandfathered">Grandfathered</option>
                    </select>
                    
                    {/* Sort */}
                    <select
                      value={`${sortBy}_${sortOrder}`}
                      onChange={(e) => {
                        const [field, order] = e.target.value.split('_');
                        setSortBy(field);
                        setSortOrder(order);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      data-testid="admin-users-sort"
                    >
                      <option value="created_at_desc">Newest signup</option>
                      <option value="created_at_asc">Oldest signup</option>
                      <option value="last_login_at_desc">Last login (recent)</option>
                      <option value="last_login_at_asc">Last login (oldest)</option>
                      <option value="last_active_at_desc">Last active (recent)</option>
                      <option value="name_asc">Name A-Z</option>
                      <option value="name_desc">Name Z-A</option>
                      <option value="email_asc">Email A-Z</option>
                    </select>
                  </div>
                </div>

                {/* Filter chips */}
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => { setInactiveOnly(v => !v); setPage(0); }}
                    data-testid="filter-inactive-30d-btn"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      inactiveOnly
                        ? 'bg-brand-accent text-white border-brand-accent'
                        : 'bg-white text-brand-ink-muted border-brand-border hover:bg-brand-accent/5 hover:text-brand-accent'
                    }`}
                  >
                    <Activity className="h-3.5 w-3.5" />
                    Inactive 30d+
                    {inactiveOnly && <span className="ml-1 opacity-80">·{total}</span>}
                  </button>
                </div>
                
                {/* Bulk Actions */}
                {selectedUsers.length > 0 && (
                  <div className="mt-4 p-4 bg-brand-accent/5 border border-brand-border rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-brand-ink">
                        {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-brand-accent mr-2">Bulk update to:</span>
                        <button onClick={() => handleBulkUpdate('trial')} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm hover:bg-yellow-200">Trial</button>
                        <button onClick={() => handleBulkUpdate('basic')} className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200">Basic</button>
                        <button onClick={() => handleBulkUpdate('premium')} className="px-3 py-1 bg-brand-accent/10 text-brand-accent rounded text-sm hover:bg-brand-accent/20">Premium</button>
                        <button onClick={() => handleBulkUpdate('grandfathered')} className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200">Grandfathered</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {usersLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedUsers.length === users.length && users.length > 0}
                              onChange={toggleSelectAll}
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Last login</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Last active</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => toggleUserSelection(user.id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-medium text-gray-900">{user.name}</p>
                                <p className="text-sm text-gray-500">{user.email}</p>
                                {user.trial_end_date && user.subscription_tier === 'trial' && (
                                  <p className="text-xs text-orange-600 mt-1">
                                    Trial ends: {new Date(user.trial_end_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${tierColors[user.subscription_tier] || tierColors.basic}`}>
                                {user.subscription_tier}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell" data-testid={`user-last-login-${user.id}`}>
                              {relativeTime(user.last_login_at)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 hidden lg:table-cell" data-testid={`user-last-active-${user.id}`}>
                              {relativeTime(user.last_active_at)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <select
                                  onChange={(e) => {
                                    if (e.target.value === 'delete') {
                                      handleDeleteUser(user.id, user.email);
                                    } else if (e.target.value.startsWith('trial_')) {
                                      const days = parseInt(e.target.value.split('_')[1]);
                                      handleSetTrialDate(user.id, days);
                                    } else {
                                      handleUpgradeUser(user.id, e.target.value);
                                    }
                                    e.target.value = '';
                                  }}
                                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                                  defaultValue=""
                                >
                                  <option value="" disabled>Actions...</option>
                                  <optgroup label="Change Tier">
                                    <option value="trial">→ Trial</option>
                                    <option value="basic">→ Basic</option>
                                    <option value="premium">→ Premium</option>
                                    <option value="grandfathered">→ Grandfathered</option>
                                  </optgroup>
                                  <optgroup label="Test Trial">
                                    <option value="trial_1">Set trial: 1 day</option>
                                    <option value="trial_7">Set trial: 7 days</option>
                                    <option value="trial_14">Set trial: 14 days</option>
                                    <option value="trial_-1">Set trial: Expired (yesterday)</option>
                                  </optgroup>
                                  <optgroup label="Danger Zone">
                                    <option value="delete" className="text-red-600">🗑️ Delete User</option>
                                  </optgroup>
                                </select>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} users
                    </p>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PrevIcon className="h-5 w-5" />
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {page + 1} of {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
